import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, Users, Utensils, ArrowRight, Check } from 'lucide-react';
import { useT } from '../i18n';

/* The three flagship managed platforms — KITXENS' commercial core.
   Consistent naming everywhere: Index AI™ · AI Workforce™ · Catering AI™ */

export const FLAGSHIPS = {
  indexai: {
    id: 'indexai',
    name: 'Index AI™',
    icon: Search,
    href: '/index-ai',
    price: 99,
    tagline: {
      en: 'Become visible in AI search — ChatGPT, Gemini and Google AI recommending your restaurant.',
      es: 'Hazte visible en la búsqueda con IA — ChatGPT, Gemini y Google AI recomendando tu restaurante.',
    },
    who: {
      en: 'For restaurants that customers can’t find when they ask AI where to eat.',
      es: 'Para restaurantes que los clientes no encuentran cuando preguntan a la IA dónde comer.',
    },
    solves: {
      en: 'AI engines now decide where people eat. If your data isn’t machine-readable, you don’t exist to them.',
      es: 'Los motores de IA ya deciden dónde come la gente. Si tus datos no son legibles por máquinas, no existes para ellos.',
    },
    includes: {
      en: ['AI-readable structured data', 'Google & Maps optimization', 'AI search monitoring', 'Monthly visibility report'],
      es: ['Datos estructurados legibles por IA', 'Optimización de Google y Maps', 'Monitoreo de búsqueda IA', 'Reporte mensual de visibilidad'],
    },
  },
  workforce: {
    id: 'workforce',
    name: 'AI Workforce™',
    icon: Users,
    href: '/ai-agents-for-restaurants',
    price: 199,
    tagline: {
      en: 'Your AI team working 24/7 — calls, reviews, reports and follow-ups without hiring more staff.',
      es: 'Tu equipo de IA trabajando 24/7 — llamadas, reseñas, reportes y seguimientos sin contratar más personal.',
    },
    who: {
      en: 'For operators drowning in repetitive work their team shouldn’t be doing.',
      es: 'Para operadores ahogados en trabajo repetitivo que su equipo no debería hacer.',
    },
    solves: {
      en: 'Missed calls, unanswered reviews and manual reporting silently cost sales every single day.',
      es: 'Llamadas perdidas, reseñas sin responder y reportes manuales cuestan ventas en silencio cada día.',
    },
    includes: {
      en: ['AI receptionist', 'Review automation', 'Automated reporting', 'Custom AI workflows'],
      es: ['Recepcionista IA', 'Automatización de reseñas', 'Reportes automáticos', 'Flujos de IA a medida'],
    },
  },
  catering: {
    id: 'catering',
    name: 'Catering AI™',
    icon: Utensils,
    href: '/ai/catering-ai',
    price: 399,
    tagline: {
      en: 'The complete AI-powered catering platform — launch, manage and grow your catering business.',
      es: 'La plataforma completa de catering con IA — lanza, gestiona y haz crecer tu negocio de catering.',
    },
    who: {
      en: 'For restaurants ready to capture $500–$5,000 corporate orders.',
      es: 'Para restaurantes listos para captar pedidos corporativos de $500–$5,000.',
    },
    solves: {
      en: 'Catering is the highest-ticket channel in food service — and most restaurants have no system for it.',
      es: 'El catering es el canal de mayor ticket en food service — y la mayoría de restaurantes no tiene sistema para captarlo.',
    },
    includes: {
      en: ['Catering ecommerce storefront', 'AI visibility for catering search', 'Quote & follow-up automation', 'Corporate outreach engine'],
      es: ['Tienda ecommerce de catering', 'Visibilidad IA para búsquedas de catering', 'Cotización y seguimiento automáticos', 'Motor de alcance corporativo'],
    },
  },
};

const CONTEXT_MAP = {
  // Index AI™ — visibility & digital presence
  'local-seo': 'indexai', 'google-business-profile': 'indexai', 'online-reputation': 'indexai',
  'restaurant-websites': 'indexai', 'digital-branding': 'indexai', 'digital-presence': 'indexai',
  'restaurant-marketing': 'indexai', 'seo': 'indexai',
  // Catering AI™
  'catering-growth': 'catering', 'catering-ai': 'catering', 'catering': 'catering',
  // AI Workforce™ — operations, tech, intelligence, growth
  'it-pos-systems': 'workforce', 'pos-management': 'workforce', 'pos-integrations': 'workforce',
  'online-ordering': 'workforce', 'data-analytics': 'workforce', 'dashboards': 'workforce',
  'performance-reporting': 'workforce', 'business-intelligence': 'workforce',
  'customer-marketing': 'workforce', 'delivery-optimization': 'workforce',
  'restaurant-revenue-growth': 'workforce', 'revenue-growth': 'workforce', 'restaurant-growth': 'workforce',
  'restaurant-technology': 'workforce', 'restaurant-intelligence': 'workforce',
  'custom-ai-automation': 'workforce', 'ai-receptionist': 'workforce', 'review-automation': 'workforce',
  'ai-solutions': 'workforce', 'ai': 'workforce', 'automation': 'workforce',
};

export function pickFlagship(context) {
  if (!context) return 'workforce';
  const key = String(context).toLowerCase().replace(/^\/+|\/+$/g, '').split('/').pop();
  return CONTEXT_MAP[key] || 'workforce';
}

/* Micro variant — elegant "Recommended next step", never a banner. */
export default function FlagshipCard({ id, context, variant = 'micro', className = '' }) {
  const { lang } = useT();
  const isEs = lang === 'es';
  const { pathname } = useLocation();
  const f = FLAGSHIPS[id || pickFlagship(context)];
  if (!f || pathname === f.href) return null;
  const Icon = f.icon;

  if (variant === 'large') {
    return (
      <div className={`flex flex-col h-full rounded-3xl border border-neutral-200 bg-white p-8 hover:border-[#F77F11] hover:shadow-[0_35px_80px_-35px_rgba(247,127,17,0.45)] transition-all ${className}`} data-testid={`flagship-large-${f.id}`}>
        <div className="w-12 h-12 rounded-2xl bg-neutral-950 text-white flex items-center justify-center mb-5">
          <Icon className="w-6 h-6" />
        </div>
        <h3 className="text-2xl font-bold text-neutral-900">{f.name}</h3>
        <p className="mt-2 text-[14.5px] text-neutral-600 leading-relaxed">{isEs ? f.tagline.es : f.tagline.en}</p>
        <p className="mt-4 text-[13px] font-semibold text-[#b45309] bg-orange-50 rounded-lg px-3 py-2">{isEs ? f.who.es : f.who.en}</p>
        <p className="mt-3 text-[13.5px] text-neutral-500 leading-relaxed">{isEs ? f.solves.es : f.solves.en}</p>
        <ul className="mt-5 space-y-2">
          {(isEs ? f.includes.es : f.includes.en).map((x) => (
            <li key={x} className="flex items-start gap-2 text-[13.5px] text-neutral-700">
              <Check className="w-4 h-4 text-[#F77F11] shrink-0 mt-0.5" /> <span className="min-w-0">{x}</span>
            </li>
          ))}
        </ul>
        <div className="mt-auto pt-6 flex items-center justify-between gap-3">
          <p className="text-neutral-900"><span className="text-[12px] text-neutral-500 block">{isEs ? 'Desde' : 'Starting from'}</span><span className="text-2xl font-bold">${f.price}</span><span className="text-sm text-neutral-500">/{isEs ? 'mes' : 'mo'}</span></p>
          <Link to={f.href} className="inline-flex items-center gap-1.5 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white px-5 h-11 font-semibold text-sm transition-colors" data-testid={`flagship-cta-${f.id}`}>
            {isEs ? 'Conocer más' : 'Learn more'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <p className="mt-3 text-[11px] text-neutral-400 leading-relaxed">
          {isEs
            ? 'El precio final depende del alcance del proyecto, número de ubicaciones y necesidades del negocio.'
            : 'Final pricing depends on project scope, number of locations and business requirements.'}
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border border-neutral-200 bg-gradient-to-br from-white to-orange-50/40 p-5 sm:p-6 ${className}`} data-testid={`flagship-micro-${f.id}`}>
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-neutral-400 mb-3">
        {isEs ? 'Siguiente paso recomendado' : 'Recommended next step'}
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <span className="w-11 h-11 rounded-xl bg-neutral-950 text-white flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-[220px]">
          <p className="font-bold text-neutral-900">{f.name}</p>
          <p className="text-[13px] text-neutral-600 leading-snug">{isEs ? f.tagline.es : f.tagline.en}</p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <p className="text-neutral-900 whitespace-nowrap"><span className="text-[11px] text-neutral-500 block leading-none mb-0.5">{isEs ? 'Desde' : 'Starting from'}</span><span className="font-bold">${f.price}</span><span className="text-[12px] text-neutral-500">/{isEs ? 'mes' : 'mo'}</span></p>
          <Link to={f.href} className="inline-flex items-center gap-1 rounded-full border border-[#F77F11] text-[#F77F11] hover:bg-[#F77F11] hover:text-white px-4 h-9 text-[13px] font-semibold transition-colors" data-testid={`flagship-micro-cta-${f.id}`}>
            {isEs ? 'Conocer más' : 'Learn more'} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
